import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Typography,
  Button,
  Input,
  Upload,
  UploadFile,
  Card,
  Skeleton,
} from "antd";

import DepartmentSelector from "../../../shared/DepartmentSelector";
import PositionSelector from "../../../shared/PositionSelector";
import UserSelector from "../../../shared/UserSelector";
import { ReactComponent as UploadIcon } from "../../../../assets/images/upload.svg";

const { Text } = Typography;
const { TextArea } = Input;
const cloudfrontKey = process.env.REACT_APP_CLOUDFRONT_KEY;

const TaskOverview = (props: any) => {
  const {
    task,
    loading,
    submitLoading,
    actions: { getTaskById, editTask },
  } = props;
  const id = useParams()?.id;
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [taskImage, setTaskImage] = useState<any>(null);
  const [imageFileList, setImageFileList] = useState<any>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [showMoreDescription, setShowMoreDescription] = useState(false);
  const charMax = 225;
  const [isEditing, setIsEditing] = useState(false);
  const isSelected =
    selectedDepartments.length > 0 ||
    selectedPositions.length > 0 ||
    selectedUsers.length > 0;

  useEffect(() => {
    getTaskById({ id });
  }, [id]);

  useEffect(() => {
    if (!task) return;

    setTitle(task.title);
    setDescription(task.description);
    setTaskImage(task.coverImageKey);
    if (task?.coverImageKey) {
      setImageFileList([
        {
          url: `${process.env.REACT_APP_CLOUDFRONT_KEY}/${task.coverImageKey}`,
        },
      ]);
    } else {
      setImageFileList([]);
    }
    setSelectedDepartments(task?.departmentIds.map((id: any) => id));
    setSelectedPositions(task?.positionIds.map((id: any) => id));
    setSelectedUsers(task?.userIds.map((id: any) => id));
  }, [task, isEditing]);

  useEffect(() => {
    isEditing && setIsEditing(false);
  }, [task]);

  const onFinish = () => {
    const formData = new FormData();

    formData.append("title", title);
    formData.append("description", description);
    if (
      imageFileList.length > 0 &&
      imageFileList[0]?.originFileObj &&
      taskImage !== task?.coverImageKey
    ) {
      formData.append("coverImage", imageFileList[0].originFileObj as File);
    }

    selectedDepartments.forEach((departmentId: string) => {
      formData.append("departmentIds", departmentId);
    });

    selectedPositions.forEach((positionId: string) => {
      formData.append("positionIds", positionId);
    });

    selectedUsers.forEach((userId: string) => {
      formData.append("userIds", userId);
    });

    editTask({
      id: task.id,
      task: formData,
    });
  };

  const handleChange = ({ fileList }: { fileList: UploadFile[] }) => {
    setImageFileList(fileList);
    setTaskImage(fileList.length > 0 ? fileList : null);
  };

  const handleDepartmentChange = (departments: string[]) => {
    setSelectedDepartments(departments);
  };

  const handlePositionChange = (positions: string[]) => {
    setSelectedPositions(positions);
  };

  const handleUserChange = (users: string[]) => {
    setSelectedUsers(users);
  };

  if (loading) {
    return <Skeleton active />;
  }

  return (
    <>
      <div className="flex flex-col-reverse xl:flex-row pb-[60px]">
        <div className="xl:w-[40%] md:py-4 xl:p-0">
          <div className=" xl:max-w-[350px] 2xl:max-w-[410px]">
            <div className="hidden xl:flex flex-col flex-wrap gap-3 mb-3">
              <Text className="text-neutral-500  font-semibold text-lg">
                Task
              </Text>
              <Text className="text-neutral-500  text-base font-normal">
                Here is the Task Overview. Keep your Task current by editing or
                updating any necessary point.
              </Text>
            </div>
          </div>
        </div>
        <div className="xl:w-[60%] xl:max-w-[535px]">
          <div className="flex flex-col xl:hidden flex-wrap gap-3 mb-3">
            <Text className="text-neutral-500  font-semibold text-lg">
              Task
            </Text>
            <Text className="text-neutral-500  text-base font-normal">
              Here is the Task Overview. Keep your Task current by editing or
              updating any necessary point.
            </Text>
          </div>

          <Card
            className={`p-6 border-none ${
              !isEditing ? "bg-white shadow-sm " : "bg-neutral-100"
            }`}
          >
            <div className="flex flex-col gap-6">
              {!isEditing && (
                <div className="flex justify-end">
                  <Button
                    onClick={() => setIsEditing(true)}
                    type="link"
                    className="text-primary-600 font-semibold text-sm p-0"
                  >
                    Edit Task
                  </Button>
                </div>
              )}

              <div>
                {!isEditing ? (
                  <>
                    <Text className="text-neutral-600 text-sm font-semibold block">
                      Task Title
                    </Text>
                    <Text className="bg-neutral-100 rounded-lg mt-2 py-4 px-6 block text-neutral-600 text-base font-semibold">
                      {title}
                    </Text>
                  </>
                ) : (
                  <>
                    <Text className="text-primary-600 text-sm font-semibold block">
                      Edit Task Title
                    </Text>
                    <Input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className=" rounded-lg mt-2 px-6 text-neutral-600 text-base font-semibold"
                    />
                  </>
                )}
              </div>

              <div>
                {!isEditing ? (
                  <>
                    <Text className="text-neutral-600 text-sm font-semibold block">
                      Description
                    </Text>
                    <div className="bg-neutral-100 rounded-lg mt-2 py-4 px-6">
                      <Text
                        className={`text-neutral-600 text-base block overflow-hidden ${
                          !showMoreDescription ? "max-h-[144px]" : "h-auto"
                        } `}
                      >
                        {!showMoreDescription
                          ? `${description.substring(0, charMax)} ${
                              description.length > charMax ? ".." : ""
                            } `
                          : description}
                      </Text>
                      {description.length > charMax && (
                        <Text
                          onClick={() =>
                            setShowMoreDescription((prev) => !prev)
                          }
                          className="text-sm text-primary-600 font-semibold block mt-2 cursor-pointer w-fit"
                        >
                          {!showMoreDescription ? "More" : "Less"}
                        </Text>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <Text className="text-primary-600 text-sm font-semibold block">
                      Edit Description
                    </Text>
                    <TextArea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Add description here"
                      autoSize={{ minRows: 6, maxRows: 10 }}
                      className="rounded-lg mt-2 text-neutral-600 text-base font-semibold"
                    />
                  </>
                )}
              </div>

              <div>
                {!isEditing ? (
                  <>
                    {taskImage && (
                      <Text className="text-neutral-600 text-sm font-semibold block">
                        Cover Image
                      </Text>
                    )}
                  </>
                ) : (
                  <Text className="text-primary-600 text-sm font-semibold block">
                    Edit Cover Image
                  </Text>
                )}
                {!isEditing ? (
                  <>
                    {taskImage && (
                      <img
                        src={`${cloudfrontKey}/${taskImage}`}
                        alt="cover"
                        className="rounded-lg w-full mt-2 h-[280px] object-cover"
                      />
                    )}
                  </>
                ) : (
                  <Upload
                    name="coverImage"
                    listType="picture-card"
                    maxCount={1}
                    beforeUpload={() => false}
                    accept="image/png, image/jpeg, image/gif"
                    className="bg-white rounded-md h-[310px] mt-2 sticky"
                    fileList={imageFileList}
                    onChange={handleChange}
                  >
                    {imageFileList.length === 0 && (
                      <div className="flex flex-col items-center justify-center">
                        <UploadIcon />
                        <p className="text-neutral-600 text-base font-medium">
                          Upload a cover image
                        </p>
                        <p className="text-xs text-neutral-500 font-normal">
                          PNG, JPG, GIF up to 5MB
                        </p>
                      </div>
                    )}
                  </Upload>
                )}
              </div>

              <div className=" flex flex-col gap-3">
                <DepartmentSelector
                  departments={task?.assignments?.departments}
                  onSelectionChange={handleDepartmentChange}
                  selectedDepartments={selectedDepartments}
                  setSelectedDepartments={setSelectedDepartments}
                  isDetailsState={!isEditing}
                  isAssignmentDetailsPage={true}
                />
                <PositionSelector
                  positions={task?.assignments?.positions}
                  onSelectionChange={handlePositionChange}
                  selectedPositions={selectedPositions}
                  setSelectedPositions={setSelectedPositions}
                  isDetailsState={!isEditing}
                  isAssignmentDetailsPage={true}
                />
                <UserSelector
                  users={task?.assignments?.users}
                  onSelectionChange={handleUserChange}
                  selectedUsers={selectedUsers}
                  setSelectedUsers={setSelectedUsers}
                  isDetailsState={!isEditing}
                  isAssignmentDetailsPage={true}
                />
              </div>
            </div>
          </Card>

          {!isEditing ? (
            <div className="w-full flex justify-end mt-10">
              <Button
                type="primary"
                onClick={() => navigate(`/educator/task/${task?.id}/approve`)}
                className="w-1/2 bg-primary-600 h-[50px]"
              >
                Approve Task
              </Button>
            </div>
          ) : (
            <div className="w-full flex justify-between gap-10 mt-10 px-6">
              <Button
                type="primary"
                onClick={() => setIsEditing(false)}
                className="w-1/2 bg-white border border-primary-200 text-primary-600 h-[50px]"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={onFinish}
                className="w-1/2 bg-primary-600 h-[50px]"
                disabled={
                  description.trim() === "" ||
                  title.trim() === "" ||
                  !taskImage ||
                  !isSelected ||
                  submitLoading
                }
                loading={submitLoading}
              >
                {!submitLoading ? "Save Changes" : "Loading"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TaskOverview;
